/**
 * State for the feng shui first run — the AI ad's landing (see
 * `./landingFlow.ts`).
 *
 * The visitor lands on the ad's "coffin position" bedroom with its issues
 * called out, one tap replays the ad's rearrangement on the canvas (no
 * sign-in, no credit) and the bed's "why" opens by itself, then they fix
 * their own room with the real AI and save it. The guide shows three
 * steps:
 *
 *   - `example`  Fix this room   — the saved example landed on the canvas
 *   - `ai`       Fix my room     — a real `ai_layout_applied` on their room
 *                                  (the goal; the first credit is free and
 *                                  needs no sign-in)
 *   - `save`     Save it         — an account (Google / Apple / email)
 *
 * Two finer beats stay recorded for the funnel without their own row:
 * `why` (the bed's rationale was seen) and `room` (their own room exists).
 *
 * Steps tick from product events observed through
 * {@link subscribeTrackedEvents}; the canvas and AI code don't know this
 * module exists. Mirrors `./collabOnboarding.ts`.
 */

import { useSyncExternalStore } from "react";
import { getFirstTouch, getLastTouch } from "./attribution";
import { isCollabOnboardingActive } from "./collabOnboarding";
import { seedFengShuiRoom, suppressWelcomeModal } from "./demoRooms";
import { clearFengShuiDemo, playFengShuiExample } from "./fengShuiDemo";
import { isFengShuiStarterRoom } from "./fengShuiExample";
import { isInAppBrowser } from "./inAppBrowser";
import { landingUtmContent } from "./landingFlow";
import { exitSharedRoom } from "./openRoom";
import { isRender3dOnboardingActive } from "./render3dOnboarding";
import { isUnstartedRoom, useDesign } from "./store";
import { getCurrentUser, hasAccount } from "./auth";
import { setFunnelStage } from "./funnelStage";
import { requestSavePrompt } from "./savePromptRequests";
import { addBreadcrumb, setUserProperty, subscribeTrackedEvents, track } from "./telemetry";

/** Delay between the first real layout landing and the "Save your room" ask. */
export const SAVE_PROMPT_DELAY_MS = 2500;
let savePromptTimer: ReturnType<typeof setTimeout> | null = null;
function scheduleSavePrompt(): void {
  if (savePromptTimer) clearTimeout(savePromptTimer);
  savePromptTimer = setTimeout(() => {
    savePromptTimer = null;
    if (!state.active || state.steps.save != null) return;
    requestSavePrompt({ surface: "feng_shui_guide" });
  }, SAVE_PROMPT_DELAY_MS);
}

export type FengShuiStep = "example" | "why" | "room" | "ai" | "save";
export const FENG_SHUI_STEPS: readonly FengShuiStep[] = ["example", "why", "room", "ai", "save"];
/** The rows the guide shows, in order. `why` / `room` are funnel-only beats. */
export const FENG_SHUI_GUIDE_STEPS: readonly FengShuiStep[] = ["example", "ai", "save"];

export type FengShuiStartPhase = "example" | "own_room";

export interface FengShuiOnboardingState {
  /** The flow was entered on this device. */
  active: boolean;
  startedAt: number | null;
  /** The landing's first call to action was answered ("Let qi flow" or
   *  "Start my own room"). Kept for funnel timing. */
  introSeenAt: number | null;
  /**
   * The visitor left the landing (the ad's rearrangement + "why" played
   * on the canvas, or they skipped it). The guide waits for it; while it is
   * `null` the landing hero owns the screen.
   */
  landingDoneAt: number | null;
  utmContent: string | null;
  /** `own_room` = arrived from the in-app browser handoff. */
  startPhase: FengShuiStartPhase;
  /** When the starter room was swapped for the visitor's own blank room. */
  ownRoomStartedAt: number | null;
  /** Consultation id of the applied example — unlocks its "Why it's here". */
  exampleConsultationId: string | null;
  /** Completion timestamps per step; `null` = not yet. */
  steps: Record<FengShuiStep, number | null>;
  /** Guide closed by the user. */
  dismissed: boolean;
  /** Set once every visible step is done. `onboarding_completed` fires
   *  exactly once when this flips. */
  completedAt: number | null;
}

const STORAGE_KEY = "room-canvas:onboarding:fengshui";

function initial(): FengShuiOnboardingState {
  return {
    active: false,
    startedAt: null,
    introSeenAt: null,
    landingDoneAt: null,
    utmContent: null,
    startPhase: "example",
    ownRoomStartedAt: null,
    exampleConsultationId: null,
    steps: { example: null, why: null, room: null, ai: null, save: null },
    dismissed: false,
    completedAt: null,
  };
}

function load(): FengShuiOnboardingState {
  if (typeof window === "undefined") return initial();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return initial();
    const parsed = JSON.parse(raw) as Partial<FengShuiOnboardingState>;
    if (!parsed || typeof parsed !== "object") return initial();
    const base = initial();
    const merged = { ...base, ...parsed, steps: { ...base.steps, ...(parsed.steps ?? {}) } };
    // Flows persisted before the landing hero existed: the full-screen
    // intro they answered counts as the landing being done.
    if (!("landingDoneAt" in parsed) && merged.introSeenAt != null) {
      merged.landingDoneAt = merged.introSeenAt;
    }
    return merged;
  } catch {
    return initial();
  }
}

let state: FengShuiOnboardingState = load();
const listeners = new Set<() => void>();

function persist(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* quota / private mode — in-memory state still works this session */
  }
}

function update(patch: Partial<FengShuiOnboardingState>): void {
  state = { ...state, ...patch };
  persist();
  for (const l of listeners) l();
}

export function getFengShuiOnboarding(): FengShuiOnboardingState {
  return state;
}

export function subscribeFengShuiOnboarding(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useFengShuiOnboarding(): FengShuiOnboardingState {
  return useSyncExternalStore(subscribeFengShuiOnboarding, getFengShuiOnboarding, getFengShuiOnboarding);
}

export function isFengShuiOnboardingActive(): boolean {
  return state.active;
}

const secondsSince = (t: number | null, now: number) =>
  t != null ? Math.max(0, Math.round((now - t) / 1000)) : null;

/**
 * Steps the guide shows. Once the visitor is in their own room, an example
 * they never watched can't be watched any more, so its row drops out.
 */
export function visibleFengShuiSteps(s: FengShuiOnboardingState = state): FengShuiStep[] {
  if (s.ownRoomStartedAt == null) return [...FENG_SHUI_GUIDE_STEPS];
  return FENG_SHUI_GUIDE_STEPS.filter((k) => k !== "example" || s.steps[k] != null);
}

export function completedFengShuiStepCount(s: FengShuiOnboardingState = state): number {
  return visibleFengShuiSteps(s).filter((k) => s.steps[k] != null).length;
}

/** The first visible step still to do, or `null` when the guide is finished. */
export function currentFengShuiStep(s: FengShuiOnboardingState = state): FengShuiStep | null {
  return visibleFengShuiSteps(s).find((k) => s.steps[k] == null) ?? null;
}

/** True while there is something left for a reopened guide to show. */
export function hasRemainingFengShuiSteps(s: FengShuiOnboardingState = state): boolean {
  return currentFengShuiStep(s) != null;
}

/**
 * Enter the flow and emit the funnel head. `example` seeds the starter
 * room; `own_room` (the in-app browser handoff) keeps the blank canvas and
 * opens the guide on "draw your room". Idempotent; returns false when
 * there was work to protect or the collab flow already owns this device.
 */
export function enterFengShuiOnboarding(opts?: {
  phase?: FengShuiStartPhase;
  utmContent?: string | null;
}): boolean {
  if (state.active) return true;
  if (isCollabOnboardingActive() || isRender3dOnboardingActive()) return false;
  const phase = opts?.phase ?? "example";
  const search = typeof window === "undefined" ? "" : window.location.search;
  const utmContent =
    opts?.utmContent ?? landingUtmContent(search, getLastTouch() ?? getFirstTouch());

  if (phase === "example") {
    if (!seedFengShuiRoom(utmContent)) return false;
  } else {
    if (!isUnstartedRoom(useDesign.getState().design.room)) return false;
    suppressWelcomeModal();
  }

  const now = Date.now();
  const inApp = isInAppBrowser();
  update({
    ...initial(),
    active: true,
    startedAt: now,
    utmContent,
    startPhase: phase,
    // The handoff visitor already saw the landing in the in-app browser.
    introSeenAt: phase === "own_room" ? now : null,
    landingDoneAt: phase === "own_room" ? now : null,
    ownRoomStartedAt: phase === "own_room" ? now : null,
  });
  ensureEventBridge();
  setUserProperty("first_run", "feng_shui");
  setUserProperty("in_app_browser", inApp);
  track("feng_shui_onboarding_shown", {
    utm_content: utmContent,
    in_app_browser: inApp,
    source: phase === "own_room" ? "handoff" : "landing",
  });
  setFunnelStage(phase === "own_room" ? "my_room" : "landing");
  addBreadcrumb("feng-shui-onboarding:entered", { utm_content: utmContent, phase });
  return true;
}

/**
 * Resume a flow carried over from an in-app browser (see `handoff.ts`):
 * the visitor already saw the landing there, so the guide opens straight
 * away on whatever step they had reached. No-op when a flow is active.
 */
export function restoreFengShuiOnboarding(carried: Partial<FengShuiOnboardingState>): boolean {
  if (state.active || isCollabOnboardingActive() || isRender3dOnboardingActive()) return false;
  const now = Date.now();
  const base = initial();
  update({
    ...base,
    active: true,
    startedAt: typeof carried.startedAt === "number" ? carried.startedAt : now,
    utmContent: typeof carried.utmContent === "string" ? carried.utmContent : null,
    startPhase: "own_room",
    introSeenAt: now,
    landingDoneAt: now,
    ownRoomStartedAt: typeof carried.ownRoomStartedAt === "number" ? carried.ownRoomStartedAt : now,
    steps: { ...base.steps, ...(carried.steps ?? {}) },
  });
  suppressWelcomeModal();
  ensureEventBridge();
  setUserProperty("first_run", "feng_shui");
  setUserProperty("in_app_browser", isInAppBrowser());
  track("feng_shui_onboarding_shown", {
    utm_content: state.utmContent,
    in_app_browser: isInAppBrowser(),
    source: "handoff",
  });
  setFunnelStage("my_room");
  return true;
}

/** Tick a step (once). */
export function completeFengShuiStep(step: FengShuiStep): void {
  if (!state.active || state.steps[step] != null) return;
  const now = Date.now();
  update({ steps: { ...state.steps, [step]: now } });
  track("onboarding_step_completed", {
    flow: "feng_shui",
    step,
    seconds_since_start: secondsSince(state.startedAt, now),
  });
  // Terminal state: no visible step left to do. Emit the funnel's
  // completion event once — the feng shui guide never fired it before.
  if (state.completedAt == null && currentFengShuiStep() == null) {
    update({ completedAt: now });
    track("onboarding_completed", {
      flow: "feng_shui",
      steps_done: completedFengShuiStepCount(),
      seconds_since_start: secondsSince(state.startedAt, now),
    });
  }
}

/**
 * Whether the landing hero (the ad's "before" room, the one-tap fix and the
 * bed's "why") owns the screen. The guide takes over once it is done.
 */
export function isFengShuiLandingPending(s: FengShuiOnboardingState = state): boolean {
  return s.active && !s.dismissed && s.landingDoneAt == null;
}

/** @deprecated alias kept for callers written against the full-screen intro. */
export const isFengShuiIntroPending = isFengShuiLandingPending;

/** Whether the guide should be on screen. */
export function isFengShuiGuideVisible(s: FengShuiOnboardingState = state): boolean {
  return s.active && !s.dismissed && s.landingDoneAt != null;
}

/**
 * Play the saved example on the canvas (the ad's rearrangement, then the
 * bed's "why"). Only meaningful while the starter room is still on the
 * canvas. Resolves with the consultation id, or `null` when nothing played.
 */
export function openFengShuiExample(source: "landing" | "guide"): Promise<string | null> {
  if (!state.active || !isFengShuiStarterRoom(useDesign.getState().design)) {
    return Promise.resolve(null);
  }
  track("feng_shui_example_opened", { source });
  return playFengShuiExample().then((id) => {
    if (id) recordFengShuiExampleApplied(id);
    return id;
  });
}

/** "Let qi flow" on the landing: play the example right there. */
export function acceptFengShuiIntro(): Promise<string | null> {
  if (!state.active || state.introSeenAt != null) return Promise.resolve(null);
  update({ introSeenAt: Date.now() });
  track("feng_shui_intro_choice", { choice: "let_qi_flow" });
  return openFengShuiExample("landing");
}

/** "Start my own room" on the landing: skip the example phase. */
export function chooseFengShuiOwnRoom(): void {
  if (!state.active || state.landingDoneAt != null) return;
  const now = Date.now();
  update({ introSeenAt: state.introSeenAt ?? now, landingDoneAt: now });
  track("feng_shui_intro_choice", { choice: "own_room" });
  clearFengShuiDemo();
  startFengShuiOwnRoom();
}

/**
 * Leave the landing hero: the guide takes over on whatever room is on the
 * canvas. `next` = "own_room" also swaps in the visitor's blank room.
 */
export function finishFengShuiLanding(next: "stay" | "own_room" = "stay"): void {
  if (!state.active || state.landingDoneAt != null) return;
  const now = Date.now();
  update({ introSeenAt: state.introSeenAt ?? now, landingDoneAt: now });
  clearFengShuiDemo();
  if (next === "own_room") startFengShuiOwnRoom();
}

/**
 * Swap the starter room for a blank one and land on the Shape view's
 * "Start your room" chooser. The guide stays open on "draw your room".
 */
export function startFengShuiOwnRoom(opts: { keepCanvas?: boolean } = {}): void {
  if (!state.active) return;
  const now = Date.now();
  update({
    ownRoomStartedAt: state.ownRoomStartedAt ?? now,
    // Leaving the starter room ends the landing whatever state it was in.
    introSeenAt: state.introSeenAt ?? now,
    landingDoneAt: state.landingDoneAt ?? now,
  });
  clearFengShuiDemo();
  setFunnelStage("my_room");
  // `keepCanvas`: the caller is about to load a room of its own (a
  // template), so don't bounce through the blank Shape view first.
  if (opts.keepCanvas) return;
  exitSharedRoom();
  useDesign.getState().resetAll();
}

/** The example was applied; remember its consultation so "Why it's here" shows for it. */
export function recordFengShuiExampleApplied(consultationId: string): void {
  if (!state.active) return;
  update({ exampleConsultationId: consultationId });
  completeFengShuiStep("example");
}

/** True for the example's consultation, even after the guide is closed. */
export function isFengShuiExampleConsultation(consultationId: string | null | undefined): boolean {
  return consultationId != null && consultationId === state.exampleConsultationId;
}

/** Close the guide ("X" / collapse on desktop is not a dismissal). */
export function dismissFengShuiGuide(): void {
  if (!state.active || state.dismissed) return;
  const now = Date.now();
  clearFengShuiDemo();
  update({
    dismissed: true,
    introSeenAt: state.introSeenAt ?? now,
    landingDoneAt: state.landingDoneAt ?? now,
  });
  track("onboarding_guide_done", {
    flow: "feng_shui",
    steps_done: completedFengShuiStepCount(),
  });
}

/** "Skip guide": leave the flow and keep whatever room is on the canvas. */
export function exitFengShuiOnboarding(method: "skip"): void {
  if (!state.active || state.dismissed) return;
  const stepsDone = completedFengShuiStepCount();
  const now = Date.now();
  clearFengShuiDemo();
  update({
    dismissed: true,
    introSeenAt: state.introSeenAt ?? now,
    landingDoneAt: state.landingDoneAt ?? now,
  });
  track("onboarding_exited", { flow: "feng_shui", method, steps_done: stepsDone });
}

/** Bring a dismissed guide back (Settings → Getting started guide). */
export function reopenFengShuiGuide(): void {
  if (!state.active || !state.dismissed) return;
  const now = Date.now();
  update({
    dismissed: false,
    introSeenAt: state.introSeenAt ?? now,
    landingDoneAt: state.landingDoneAt ?? now,
  });
}

// ---------------------------------------------------------------------------
// Event bridge — product events → guide steps
// ---------------------------------------------------------------------------

let bridgeUnsub: (() => void) | null = null;

export function ensureEventBridge(): void {
  if (bridgeUnsub) return;
  bridgeUnsub = subscribeTrackedEvents((name, props) => {
    if (!state.active) return;
    if (name === "feng_shui_example_applied") {
      completeFengShuiStep("example");
      setFunnelStage("example_applied");
    } else if (name === "ai_rationale_viewed" && props?.source === "example") {
      completeFengShuiStep("why");
    } else if (name === "room_created" && state.ownRoomStartedAt != null) {
      completeFengShuiStep("room");
    } else if (name === "ai_layout_applied") {
      // Real layouts only — the example fires `feng_shui_example_applied`.
      const first = state.steps.ai == null;
      completeFengShuiStep("ai");
      setFunnelStage("ai_applied");
      // The post-value ask: once the visitor has seen the AI work on their
      // own room, offer to keep it. A beat later, so the layout lands first.
      if (first && state.steps.save == null && !hasAccount(getCurrentUser())) {
        scheduleSavePrompt();
      }
    } else if (name === "sign_up" || name === "login" || name === "account_linked") {
      completeFengShuiStep("save");
      setFunnelStage("done");
    } else if (name === "save_prompt_shown") {
      setFunnelStage("save");
    }
  });
}

// Boot: if a previous page load entered the flow, keep listening.
if (state.active) ensureEventBridge();

/** Test-only: wipe memory + storage. */
export function _resetFengShuiOnboardingForTests(): void {
  if (bridgeUnsub) bridgeUnsub();
  bridgeUnsub = null;
  if (savePromptTimer) clearTimeout(savePromptTimer);
  savePromptTimer = null;
  state = initial();
  if (typeof window !== "undefined") {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }
  listeners.clear();
}
